<template>
    <form class="community-thread-editor" @submit.prevent="emitSave">
        <div class="stepper-wrapper">
            <div class="step-nav-wrapper">
                <stepper v-model:value="activeStep" :linear="!isEditingThread">
                    <step-list>
                        <step v-for="step in wizardSteps" :key="step.value" :value="step.value">
                            {{ step.name }}
                        </step>
                    </step-list>
                </stepper>
            </div>
            <stepper class="stepper-content" v-model:value="activeStep" :linear="!isEditingThread">
                <step-panels>
                    <step-panel :value="TYPE_STEP">
                        <div class="editor-content editor-content-step">
                            <input-with-label label="Thread type" for-id="communityThreadType">
                                <primevue-select-button
                                    id="communityThreadType"
                                    v-model="form.reportType"
                                    :allow-empty="false"
                                    :options="THREAD_TYPE_OPTIONS"
                                    option-label="name"
                                    option-value="id"
                                />
                            </input-with-label>
                        </div>
                    </step-panel>
                    <step-panel :value="CONTENT_STEP">
                        <div class="editor-content editor-content-step">
                            <template v-if="isLinkedInThread">
                                <input-with-label label="Title" for-id="communityThreadTitle">
                                    <input-text id="communityThreadTitle" v-model="form.title" :maxlength="COMMUNITY_THREAD_TITLE_MAX_LENGTH" />
                                </input-with-label>

                                <input-with-label label="LinkedIn share link" for-id="communityThreadLinkedInUrl">
                                    <input-text id="communityThreadLinkedInUrl" v-model="form.linkedinUrl" />
                                </input-with-label>

                                <input-with-label label="LinkedIn embed link" for-id="communityThreadLinkedInEmbedUrl">
                                    <input-text id="communityThreadLinkedInEmbedUrl" v-model="form.linkedinEmbedUrl" />
                                </input-with-label>
                            </template>
                            <template v-else>
                                <input-with-label label="Title" for-id="communityThreadTitle">
                                    <input-text id="communityThreadTitle" v-model="form.title" :maxlength="COMMUNITY_THREAD_TITLE_MAX_LENGTH" />
                                </input-with-label>

                                <input-with-label label="Description" for-id="communityThreadDescription">
                                    <primevue-textarea id="communityThreadDescription" v-model="form.description" rows="3" :maxlength="COMMUNITY_THREAD_DESCRIPTION_MAX_LENGTH" />
                                </input-with-label>

                                <input-with-label label="Body" class="body-input-wrapper">
                                    <rich-text-editor
                                        ref="bodyEditor"
                                        v-model="form.bodyHtml"
                                        class="body-editor"
                                    />
                                </input-with-label>
                            </template>
                        </div>
                    </step-panel>
                    <step-panel :value="METADATA_STEP">
                        <div class="editor-content">
                            <input-with-label label="Authors" for-id="communityThreadAuthors">
                                <spark-multi-select
                                    id="communityThreadAuthors"
                                    v-model="form.authors"
                                    option-label="name"
                                    option-value="id"
                                    :options="authorsOptions"
                                    :max-selected-labels="5"
                                    placeholder="Select authors"
                                />
                            </input-with-label>

                            <input-with-label label="Publication date" for-id="communityThreadPublicationDate">
                                <div class="publication-date-input">
                                    <basic-date-picker
                                        v-model="publicationDate"
                                        :show-time="false"
                                        :show-seconds="false"
                                        placeholder="Select date"
                                        name="communityThreadPublicationDate"
                                    />
                                </div>
                            </input-with-label>

                            <input-with-label label="Site area relevance">
                                <site-area-relevance-selector
                                    v-model="form.siteAreaRelevance"
                                    :options="siteAreaRelevanceOptions"
                                />
                            </input-with-label>

                            <div class="advanced-divider">
                                <span>Advanced</span>
                            </div>

                            <div class="flex-row-with-gap">
                                <checkbox
                                    v-model="linkToSiteArea"
                                    binary
                                    input-id="communityThreadLinkToSiteArea"
                                />
                                <label for="communityThreadLinkToSiteArea" class="flex-row-with-gap">
                                    <div>Link to site area?</div>
                                    <info-icon-with-tooltip tooltip-text="Linking to a site area will make this content appear in the 'page feed' section of notifications." />
                                </label>
                            </div>

                            <input-with-label v-if="linkToSiteArea" label="Site area links" optional>
                                <prime-tree
                                    v-model:expandedKeys="expandedPageLinkKeys"
                                    v-model:selectionKeys="selectedPageLinkKeys"
                                    :value="COMMUNITY_PAGE_LINK_TREE"
                                    selection-mode="checkbox"
                                    :propagate-selection-up="false"
                                    :propagate-selection-down="true"
                                    class="community-page-link-tree"
                                />
                            </input-with-label>
                        </div>
                    </step-panel>
                    <step-panel v-if="isStandardThread" :value="IMAGE_STEP">
                        <div class="editor-content">
                            <input-with-label label="Thumbnail" optional>
                                <div class="image-upload-row">
                                    <file-upload
                                        mode="basic"
                                        accept="image/*"
                                        :auto="false"
                                        :custom-upload="true"
                                        choose-label="Choose image"
                                        @select="handleThumbnailSelection"
                                    />
                                    <span v-if="selectedThumbnailFile">{{ selectedThumbnailFile.name }}</span>
                                    <span v-else-if="props.thread?.thumbnailUrl">Uploading a new image will replace the current thumbnail.</span>
                                    <span v-else>Fallback thumbnail will be used if no image is selected.</span>
                                </div>
                                <div v-if="thumbnailImageUrl" class="thumbnail-preview">
                                    <img
                                        :src="thumbnailImageUrl"
                                        alt="Thread thumbnail preview"
                                        @error="handleThumbnailPreviewError"
                                    />
                                </div>
                            </input-with-label>
                        </div>
                    </step-panel>
                    <step-panel :value="PREVIEW_STEP">
                        <div class="preview-step">
                            <spark-content-viewer
                                :spark-content-details="previewSparkContentDetails"
                                display-mode="modal"
                                no-breadcrumb
                                preview-mode
                            />
                        </div>
                    </step-panel>
                </step-panels>
            </stepper>
        </div>

        <title-bar class="editor-actions">
            <div class="footer-wrapper">
                <div class="editor-primary-actions">
                    <prime-button v-if="props.canDelete" text @click="emit('delete')" class="plain-btn" :disabled="props.isSaving">
                        Delete
                        <font-awesome-icon :icon="['fas', 'trash-can']" />
                    </prime-button>
                    <prime-button v-if="canShowSave" text class="plain-btn community-save-button" @click="emitSave" :disabled="saveDisabled || props.isSaving">
                        <div v-if="props.isSaving" class="loader-container">
                            <component-loader />
                        </div>
                        <div v-else class="flex-row-with-gap">
                            Save
                            <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                        </div>
                    </prime-button>
                    <prime-button
                        v-if="activeStep !== lastStepValue"
                        text
                        class="next plain-btn"
                        :disabled="isNextDisabled"
                        @click="goNextStep"
                    >
                        Next
                        <font-awesome-icon :icon="['fas', 'chevron-right']" />
                    </prime-button>
                </div>
                <prime-button
                    v-if="activeStep !== firstStepValue"
                    text
                    class="plain-btn"
                    @click="goPreviousStep"
                >
                    <font-awesome-icon :icon="['fas', 'chevron-left']" />
                    Previous
                </prime-button>
            </div>
        </title-bar>
    </form>
</template>

<script setup lang="ts">
import {
    computed, onBeforeUnmount, ref,
} from 'vue';
import FileUpload, { FileUploadSelectEvent } from 'primevue/fileupload';
import InputText from 'primevue/inputtext';
import PrimeButton from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import PrimevueSelectButton from 'primevue/selectbutton';
import PrimevueTextarea from 'primevue/textarea';
import PrimeTree from 'primevue/tree';
import Step from 'primevue/step';
import StepList from 'primevue/steplist';
import StepPanel from 'primevue/steppanel';
import StepPanels from 'primevue/steppanels';
import Stepper from 'primevue/stepper';
import SparkContentViewer from '@/components/deltaGpt/SparkContentViewer.vue';
import BasicDatePicker from '@/components/powerMarkets/common/BasicDatePicker.vue';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import SiteAreaRelevanceSelector from '@/components/community/SiteAreaRelevanceSelector.vue';
import InputWithLabel from '@/components/common/InputWithLabel.vue';
import RichTextEditor from '@/components/common/RichTextEditor.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import TitleBar from '@/components/layout/TitleBar.vue';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import { IAccess, ISparkContentDetails, ThreadReportType } from '@/interfaces/deltaGpt/IDocument.ts';
import { IWizardStep } from '@/interfaces/general/IWizardStep.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { SITE_AREA_RELEVANCE_OPTIONS } from '@/utils/siteAreaRelevanceUtils.ts';
import { isLinkedInReportType } from '@/utils/researchContent.ts';
import { isRichTextHtmlEmpty } from '@/utils/htmlUtils.ts';
import { parsePublicationDate, serializePublicationDateForSave } from '@/utils/sparkContentUtils.ts';
import {
    ICommunityThreadDetails,
    ICommunityThreadPageLink,
    ICommunityThreadSaveRequest,
} from '@/interfaces/community/ICommunityThread.ts';
import {
    COMMUNITY_PAGE_LINK_TREE,
    collapseCommunityPageLinkDefinitions,
    createCommunityThreadPageLink,
    getCommunityPageLinkDefinitionByRouteName,
} from '@/utils/communityPageLinkRegistry.ts';
import { useConfirm } from 'primevue/useconfirm';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';

const COMMUNITY_THREAD_TITLE_MAX_LENGTH = 120;
const COMMUNITY_THREAD_DESCRIPTION_MAX_LENGTH = 300;
const THREAD_TYPE_OPTIONS: Array<{ id: ThreadReportType; name: string }> = [
    { id: 'Feature', name: 'Feature' },
    { id: 'Post', name: 'Post' },
    { id: 'LinkedIn', name: 'LinkedIn' },
];

const TYPE_STEP = 1;
const CONTENT_STEP = 2;
const METADATA_STEP = 3;
const IMAGE_STEP = 4;
const PREVIEW_STEP = 5;

const TYPE_WIZARD_STEP: IWizardStep = { name: 'Thread type', value: TYPE_STEP };
const CONTENT_WIZARD_STEP: IWizardStep = { name: 'Content', value: CONTENT_STEP };
const METADATA_WIZARD_STEP: IWizardStep = { name: 'Metadata', value: METADATA_STEP };
const IMAGE_WIZARD_STEP: IWizardStep = { name: 'Image', value: IMAGE_STEP };
const PREVIEW_WIZARD_STEP: IWizardStep = { name: 'Preview', value: PREVIEW_STEP };

const props = defineProps<{
    thread?: ICommunityThreadDetails | null;
    isSaving: boolean;
    canDelete: boolean;
}>();

const emit = defineEmits<{
    save: [ICommunityThreadSaveRequest];
    delete: [];
}>();

type TPageLinkSelectionKeys = Record<string, { checked: boolean; partialChecked: boolean }>;
type TPageLinkExpandedKeys = Record<string, boolean>;

const centralModalStore = useCentralModalStore();
const confirm = useConfirm();

const activeStep = ref<number>(1);
const form = ref<ICommunityThreadSaveRequest>(createForm(props.thread));
const publicationDate = ref<Date | null>(getInitialPublicationDate(props.thread));
const initialPageLinks = props.thread?.pageLinks ?? [];
const linkToSiteArea = ref<boolean>(initialPageLinks.length > 0);
const selectedPageLinkKeys = ref<TPageLinkSelectionKeys>(createPageLinkSelection(initialPageLinks));
const expandedPageLinkKeys = ref<TPageLinkExpandedKeys>(createPageLinkExpandedKeys(initialPageLinks));
const selectedThumbnailFile = ref<File | null>(null);
const thumbnailPreviewUrl = ref<string | null>(null);
const thumbnailPreviewFailed = ref<boolean>(false);
const siteAreaRelevanceOptions = SITE_AREA_RELEVANCE_OPTIONS;
const analystAuthorOptions = ref<Array<IIdName>>([]);
const bodyEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);
let allowNextModalClose = false;
let disposeModalCloseGuard: (() => void) | null = null;
const isLinkedInThread = computed<boolean>(() => isLinkedInReportType(form.value.reportType));
const isStandardThread = computed<boolean>(() => !isLinkedInThread.value);
const wizardSteps = computed<Array<IWizardStep>>(() => (
    isLinkedInThread.value
        ? [TYPE_WIZARD_STEP, CONTENT_WIZARD_STEP, METADATA_WIZARD_STEP, PREVIEW_WIZARD_STEP]
        : [TYPE_WIZARD_STEP, CONTENT_WIZARD_STEP, METADATA_WIZARD_STEP, IMAGE_WIZARD_STEP, PREVIEW_WIZARD_STEP]
));
const firstStepValue = computed<number>(() => wizardSteps.value[0].value);
const lastStepValue = computed<number>(() => wizardSteps.value[wizardSteps.value.length - 1].value);
const activeStepIndex = computed<number>(() => wizardSteps.value.findIndex(step => step.value === activeStep.value));
const authorsOptions = computed<Array<IIdName>>(() => analystAuthorOptions.value);

const thumbnailImageUrl = computed<string | null>(() => {
    if (thumbnailPreviewFailed.value) return null;
    return thumbnailPreviewUrl.value ?? props.thread?.thumbnailUrl ?? null;
});
const previewFullImageUrl = computed<string>(() => thumbnailPreviewUrl.value ?? props.thread?.fullImageUrl ?? '');
const isEditingThread = computed<boolean>(() => !!props.thread?.threadId);
const selectedPageLinks = computed<Array<ICommunityThreadPageLink>>(() => (linkToSiteArea.value && selectedPageLinkKeys.value
    ? collapseCommunityPageLinkDefinitions(Object.entries(selectedPageLinkKeys.value)
        .filter(([, selection]) => selection.checked)
        .map(([routeName]) => getCommunityPageLinkDefinitionByRouteName(routeName))
        .filter((pageLink): pageLink is NonNullable<typeof pageLink> => !!pageLink))
        .map(createCommunityThreadPageLink)
    : []));
const typeStepIsValid = computed<boolean>(() => THREAD_TYPE_OPTIONS.some(option => option.id === form.value.reportType));
const titleIsValid = computed<boolean>(() => (
    !!form.value.title.trim()
    && form.value.title.trim().length <= COMMUNITY_THREAD_TITLE_MAX_LENGTH
));
const titleAndDescriptionAreValid = computed<boolean>(() => (
    titleIsValid.value
    && !!form.value.description.trim()
    && form.value.description.trim().length <= COMMUNITY_THREAD_DESCRIPTION_MAX_LENGTH
));
const linkedInContentStepIsValid = computed<boolean>(() => (
    titleIsValid.value
    && isValidLinkedInShareLink(form.value.linkedinUrl)
    && !!getCleanLinkedInEmbedUrl(form.value.linkedinEmbedUrl)
));
const contentStepIsValid = computed<boolean>(() => (
    isLinkedInThread.value
        ? linkedInContentStepIsValid.value
        : titleAndDescriptionAreValid.value && !isRichTextHtmlEmpty(form.value.bodyHtml)
));
const authorsAreValid = computed<boolean>(() => form.value.authors.some(author => author.trim().length > 0));
const siteAreaRelevanceIsValid = computed<boolean>(() => form.value.siteAreaRelevance.length > 0);
const metadataStepIsValid = computed<boolean>(() => authorsAreValid.value && siteAreaRelevanceIsValid.value);
const saveDisabled = computed<boolean>(() => (
    props.isSaving
    || !typeStepIsValid.value
    || !contentStepIsValid.value
    || !metadataStepIsValid.value
));
const isNextDisabled = computed<boolean>(() => (
    (activeStep.value === TYPE_STEP && !typeStepIsValid.value)
    || (activeStep.value === CONTENT_STEP && !contentStepIsValid.value)
    || (activeStep.value === METADATA_STEP && !metadataStepIsValid.value)
));
const canShowSave = computed<boolean>(() => isEditingThread.value || activeStep.value === lastStepValue.value);
const previewSparkContentDetails = computed<ISparkContentDetails>(() => ({
    authors: form.value.authors,
    documentShortCode: props.thread?.threadId ?? 'community-thread-preview',
    documentTitle: form.value.title || 'Untitled thread',
    hasAccess: true,
    publicationDate: formatPublicationDateForSave(publicationDate.value),
    filters: [],
    description: isLinkedInThread.value ? '' : form.value.description || 'No description provided.',
    accessReason: 'Public',
    reportType: form.value.reportType,
    bodyHtml: isStandardThread.value ? form.value.bodyHtml : '',
    thumbnailUrl: thumbnailImageUrl.value ?? '',
    fullImageUrl: previewFullImageUrl.value,
    linkedinUrl: isLinkedInThread.value ? form.value.linkedinUrl ?? null : null,
    linkedinEmbedUrl: isLinkedInThread.value ? getCleanLinkedInEmbedUrl(form.value.linkedinEmbedUrl) : null,
    updatedAt: props.thread?.updatedAt,
    commentCount: props.thread?.commentCount ?? 0,
    routeNames: selectedPageLinks.value.map(pageLink => pageLink.routeName),
    siteAreaRelevance: form.value.siteAreaRelevance,
}));
const initialEditorSnapshot = createEditorSnapshot();
const areActiveEdits = computed<boolean>(() => createEditorSnapshot() !== initialEditorSnapshot);

function createForm (thread?: ICommunityThreadDetails | null): ICommunityThreadSaveRequest {
    return {
        documentShortCode: thread?.threadId ?? '',
        title: thread?.title ?? '',
        subtitle: null,
        description: thread?.description ?? '',
        authors: getInitialAuthors(thread),
        bodyHtml: thread && isLinkedInReportType(thread.reportType) ? '' : (thread?.bodyHtml ?? ''),
        reportType: thread?.reportType ?? 'Post',
        pageLinks: [],
        access: createDefaultPublicAccess(),
        siteAreaRelevance: thread?.siteAreaRelevance ?? [],
        publicationDate: formatPublicationDateForSave(getInitialPublicationDate(thread)),
        linkedinUrl: thread?.linkedinUrl ?? '',
        linkedinEmbedUrl: thread?.linkedinEmbedUrl ?? '',
        isDraft: false,
        publishAt: null,
    };
}

function getInitialAuthors (thread?: ICommunityThreadDetails | null): Array<string> {
    if (thread?.authors?.length) return thread.authors;
    return thread?.authorName ? [thread.authorName] : [];
}
function createDefaultPublicAccess (): IAccess {
    return {
        isHidden: false,
        hiddenIfNoAccess: false,
        isPublic: true,
        isTrial: false,
        isObsolete: false,
        access: [],
        enactRequirement: [],
        enactAccessRequirements: [],
    };
}

function isValidLinkedInShareLink (url: string | null | undefined): boolean {
    const trimmedUrl = url?.trim();
    if (!trimmedUrl) return false;

    try {
        const parsedUrl = new URL(trimmedUrl);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
        return false;
    }
}

function getCleanLinkedInEmbedUrl (urlOrIframe: string | null | undefined): string | null {
    const trimmedValue = urlOrIframe?.trim();
    if (!trimmedValue) return null;

    const iframeSrc = trimmedValue.match(/\ssrc=["']([^"']+)["']/i)?.[1];
    const rawUrl = iframeSrc ?? trimmedValue;

    try {
        const parsedUrl = new URL(rawUrl);
        if (parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== 'www.linkedin.com') return null;
        if (!parsedUrl.pathname.startsWith('/embed/feed/update/')) return null;
        parsedUrl.search = '';
        parsedUrl.hash = '';
        return parsedUrl.toString();
    } catch {
        return null;
    }
}

async function initialiseAuthorsList (): Promise<void> {
    const analysts = await deltaGptApi.getAllAnalysts();
    analystAuthorOptions.value = analysts.map(analyst => ({
        id: analyst.name,
        name: analyst.name,
    }));
}

function createPageLinkSelection (pageLinks: Array<ICommunityThreadPageLink>): TPageLinkSelectionKeys {
    const selection: TPageLinkSelectionKeys = {};

    pageLinks.forEach(pageLink => {
        const definition = getCommunityPageLinkDefinitionByRouteName(pageLink.routeName);

        if (!definition) {
            selection[pageLink.routeName] = { checked: true, partialChecked: false };
            return;
        }

        selection[pageLink.routeName] = { checked: true, partialChecked: false };

        definition.descendantRouteNames.forEach(descendantRouteName => {
            selection[descendantRouteName] = { checked: true, partialChecked: false };
        });

        definition.ancestorRouteNames.forEach(ancestorRouteName => {
            if (selection[ancestorRouteName]?.checked) return;
            selection[ancestorRouteName] = { checked: false, partialChecked: true };
        });
    });

    return selection;
}

function createPageLinkExpandedKeys (pageLinks: Array<ICommunityThreadPageLink>): TPageLinkExpandedKeys {
    const expandedKeys: TPageLinkExpandedKeys = {};

    pageLinks.forEach(pageLink => {
        const definition = getCommunityPageLinkDefinitionByRouteName(pageLink.routeName);
        if (!definition) return;

        definition.ancestorRouteNames.forEach(routeName => {
            expandedKeys[routeName] = true;
        });
    });

    return expandedKeys;
}

function getInitialPublicationDate (thread?: ICommunityThreadDetails | null): Date {
    return parsePublicationDate(thread?.publicationDate)?.toDate() ?? new Date();
}

function formatPublicationDateForSave (date: Date | null): string {
    return serializePublicationDateForSave(date);
}

function emitSave (): void {
    if (!canShowSave.value) return;
    if (saveDisabled.value) return;

    if (!isEditingThread.value) {
        confirm.require({
            message: 'When published, this thread will be publicly available and visible to all Spark users.<br/><br/>Are you sure you want to proceed?',
            accept: saveThread,
        });
        return;
    }

    saveThread();
}

function saveThread (): void {
    emit('save', {
        ...form.value,
        title: form.value.title.trim(),
        subtitle: null,
        description: isLinkedInThread.value ? '' : form.value.description.trim(),
        authors: form.value.authors.map(author => author.trim()).filter(Boolean),
        bodyHtml: isStandardThread.value ? form.value.bodyHtml : '',
        reportType: form.value.reportType,
        pageLinks: selectedPageLinks.value,
        access: form.value.access,
        siteAreaRelevance: form.value.siteAreaRelevance,
        publicationDate: formatPublicationDateForSave(publicationDate.value),
        linkedinUrl: isLinkedInThread.value ? form.value.linkedinUrl?.trim() ?? '' : '',
        linkedinEmbedUrl: isLinkedInThread.value ? getCleanLinkedInEmbedUrl(form.value.linkedinEmbedUrl) ?? '' : '',
        thumbnailFile: isStandardThread.value ? selectedThumbnailFile.value : null,
        richTextImages: isStandardThread.value ? bodyEditor.value?.getPendingImages() ?? [] : [],
    } as ICommunityThreadSaveRequest);
}

function createEditorSnapshot (): string {
    return JSON.stringify({
        form: form.value,
        publicationDate: formatPublicationDateForSave(publicationDate.value),
        linkToSiteArea: linkToSiteArea.value,
        selectedPageLinkKeys: selectedPageLinkKeys.value,
        selectedThumbnailFileName: selectedThumbnailFile.value?.name ?? null,
    });
}

function handleBeforeUnload (event: BeforeUnloadEvent): void {
    if (!areActiveEdits.value) return;
    event.preventDefault();
}

function confirmDiscardChanges (): void {
    if (!areActiveEdits.value) {
        allowNextModalClose = true;
        centralModalStore.closeCurrentModal();
        return;
    }

    confirm.require({
        message: 'If you leave now, your changes will not be saved.<br/><br/> Would you still like to leave?',
        accept: () => {
            allowNextModalClose = true;
            centralModalStore.closeCurrentModal();
        },
    });
}

function handleModalCloseGuard (): boolean {
    if (allowNextModalClose || props.isSaving || !areActiveEdits.value) return true;
    confirmDiscardChanges();
    return false;
}

function goPreviousStep (): void {
    if (activeStep.value === firstStepValue.value) return;
    activeStep.value = wizardSteps.value[Math.max(activeStepIndex.value - 1, 0)].value;
}

function goNextStep (): void {
    if (activeStep.value === lastStepValue.value || isNextDisabled.value) return;
    activeStep.value = wizardSteps.value[Math.min(activeStepIndex.value + 1, wizardSteps.value.length - 1)].value;
}

function handleThumbnailSelection (event: FileUploadSelectEvent): void {
    selectedThumbnailFile.value = event.files[0] ?? null;
    thumbnailPreviewFailed.value = false;

    if (thumbnailPreviewUrl.value) {
        URL.revokeObjectURL(thumbnailPreviewUrl.value);
    }

    thumbnailPreviewUrl.value = selectedThumbnailFile.value ? URL.createObjectURL(selectedThumbnailFile.value) : null;
}

function handleThumbnailPreviewError (): void {
    thumbnailPreviewFailed.value = true;
}

onBeforeUnmount(() => {
    if (thumbnailPreviewUrl.value) {
        URL.revokeObjectURL(thumbnailPreviewUrl.value);
    }
    window.removeEventListener('beforeunload', handleBeforeUnload);
    disposeModalCloseGuard?.();
});

window.addEventListener('beforeunload', handleBeforeUnload);
disposeModalCloseGuard = centralModalStore.registerModalCloseGuard(handleModalCloseGuard);

initialiseAuthorsList().catch(() => {
    analystAuthorOptions.value = [];
});

defineExpose({
    handleCancelClick: confirmDiscardChanges,
});
</script>

<style scoped lang="scss">
@use '/src/assets/scss/components/common/wizard.scss';

.community-thread-editor {
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.stepper-wrapper {
    min-height: 0;
}

.stepper-content,
.stepper-content :deep(.p-steppanels),
.stepper-content :deep(.p-steppanel) {
    min-height: 0;
    overflow: hidden;
}

.stepper-content :deep(.p-steppanel-content) {
    height: 100%;
    min-height: 0;
}

.editor-content {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    height: 100%;
    min-height: 0;
    overflow: auto;
    padding: var(--largeSpacing);
}

.publication-date-input {
    width: 16rem;
    max-width: 100%;
}

.advanced-divider {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    color: var(--neutral600);
    font-size: var(--extraSmallFontSize);
    text-transform: uppercase;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--neutral300);
    }
}

.editor-content-step {
    overflow: hidden;
}

.body-input-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
}

.body-input-wrapper :deep(.input-content),
.body-editor {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
}

.body-editor :deep(.p-editor-container) {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
}

.body-editor :deep(.p-editor-content) {
    flex: 1;
    min-height: 0;
}

.body-editor :deep(.ql-container) {
    height: 100%;
}

.editor-actions {
    justify-content: flex-end;
    border-top: 1px solid var(--neutral300);
    border-bottom: 0;
}

.editor-primary-actions {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
}

.community-save-button {
    background-color: var(--actionColour);
    color: var(--neutral0);

    .loader-container {
        height: 100%;
        padding: var(--smallSpacing);
    }

    svg {
        color: var(--neutral0);
    }

    &:enabled:hover {
        background-color: var(--accentColour);
    }
}

.preview-step {
    display: flex;
    box-sizing: border-box;
    height: 100%;
    min-height: 0;
    overflow: hidden;

    :deep(.document-viewer) {
        flex: 1;
        min-height: 0;
    }
}

.image-upload-row {
    display: flex;
    align-items: center;
    gap: var(--defaultSpacing);
    flex-wrap: wrap;

    span {
        color: var(--neutral700);
        font-size: var(--smallFontSize);
        font-weight: var(--normalFontWeight);
    }
}

.community-page-link-tree {
    background: var(--neutral100);
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusInner);
    padding: var(--smallSpacing);

    :deep(.p-tree-node-content) {
        padding: var(--smallSpacing);
    }

    :deep(.p-tree-node-content:has(.p-checkbox-checked, .p-checkbox[data-p-partialchecked='true']) > .p-tree-node-toggle-button) {
        color: var(--accentColour);
    }

    :deep(.p-tree-node-children) {
        margin-left: var(--largeSpacing);
    }
}

.thumbnail-preview {
    width: min(18rem, 100%);
    aspect-ratio: 4 / 3;
    overflow: hidden;
    border: 1px solid var(--neutral300);
    border-radius: var(--borderRadiusInner);
    background: var(--neutral200);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}
</style>
